import { Mail } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "@/components/Nav";

const SECTION_LINKS = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

const PAGE_LINKS = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Use", to: "/terms" },
  { label: "Disclaimer", to: "/disclaimer" },
];

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();

  const goSection = (e, href) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(href), 500);
    } else {
      scrollToSection(href);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-navy grain relative border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 bg-white/10 border border-white/15 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="font-display font-bold text-lg tracking-tight text-white">Certify IP Global</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm mb-6">
              IP Data Validation &amp; Patent Information Verification
            </p>
            <a
              href="mailto:info@certifyipglobal.com"
              data-testid="footer-email-link"
              className="inline-flex items-center gap-2.5 text-sm text-slate-300 hover:text-white transition-colors duration-300 group"
            >
              <Mail className="w-4 h-4 text-blue-400" />
              <span className="font-monodata border-b border-transparent group-hover:border-blue-400 transition-colors duration-300">
                info@certifyipglobal.com
              </span>
            </a>
          </div>

          <div className="lg:col-span-4">
            <p className="font-monodata text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-6">Navigate</p>
            <nav className="grid grid-cols-2 gap-x-8 gap-y-3" aria-label="Footer">
              {SECTION_LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => goSection(e, l.href)}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-slate-400 hover:text-white transition-colors duration-300"
                >
                  {l.label}
                </a>
              ))}
              {PAGE_LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-slate-400 hover:text-white transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="lg:col-span-3">
            <p className="font-monodata text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-6">Focus</p>
            <ul className="space-y-3 text-sm text-slate-400">
              <li>US Patent Data Validation</li>
              <li>Patent Deadline Verification</li>
              <li>IP Portfolio Data Quality</li>
              <li>IP Database Validation</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
          <p className="text-xs text-slate-500" data-testid="footer-copyright">
            © 2026 Certify IP Global. All rights reserved.
          </p>
          <p className="text-xs text-slate-600 max-w-xl leading-relaxed">
            Certify IP Global is not a law firm and does not provide legal advice. Patent
            deadlines and legal rights should be independently confirmed by a qualified patent
            attorney or authorized IP professional.
          </p>
        </div>
      </div>
    </footer>
  );
}
